import { extractAvgColor, calculateAverageRGB, calculateContrast } from "../utilities/color";

import { BaseModule } from "./base.module";
import { DebounceModule } from './debounce.module';
import Color from 'colorjs.io';

export class ColorModule extends BaseModule {
    configuration = {};
    header = document.querySelector('header');

    constructor(extraConfiguration = {}) {
        super();

        this.configuration = Object.assign(this.configuration, extraConfiguration);
        this.debounceModule = new DebounceModule();

        this.lightThemeClass = "dynamic-light";
        this.darkThemeClass = "dynamic-dark";

        this.setEventListeners();
    }

    setEventListeners() {
        if (typeof document !== "undefined") {
            let configurationElement = document;
            this.triggerEventOnElement("constructor", configurationElement, this);
            this.triggerEventOnElement(
                "constructor.color-module",
                configurationElement,
                this
            );
            // resize listener, because of multiple images that may be loaded in different screen sizes
            if (window !== "undefined") {
                window.addEventListener(
                    "resize",
                    this.debounceModule.debounce((event) => {
                        // this.handleLoad();
                    }, 400)
                );
            }
        }
    }

    initialize() {
        
        this._minimumContrastRatio = this.getMinimumContrastRatio();
        console.log('_minimumContrastRatio', this._minimumContrastRatio);

        window.addEventListener("load", () => {
            this.handleLoad("video");
        });

        // lazy load target can be any image in the page, there is no promised order
        document.addEventListener("lazyloaded", (lazyLoadEvent) => {
            if (
                lazyLoadEvent.target &&
                lazyLoadEvent.target.dataset &&
                lazyLoadEvent.target.dataset.bg
            ) {
                let backgroundElement = lazyLoadEvent.target;
                if (
                    backgroundElement &&
                    backgroundElement.dataset &&
                    backgroundElement.dataset.image
                ) {
                    // console.log('lazyloaded', lazyLoadEvent);
                    // if the jumbotron contains background element
                    // if background element has dataset with 'image' property
                    const computedBackgroundImage = getComputedStyle(backgroundElement).backgroundImage;
                    // console.log('computedBackgroundImage', computedBackgroundImage);
                    if (computedBackgroundImage !== "none") {
                        // let element = document.querySelector('.jumbotron');
                        let element = backgroundElement.closest('.jumbotron');
                        this.measureImage(backgroundElement, element, true);
                    }
                }
            }
        });
    }

    handleLoad(mediaType) {
        if (typeof document !== "undefined") {
            const videoJumbotrons = document.querySelectorAll(".jumbotron-video");
            // console.log('videoJumbotrons', videoJumbotrons);

            videoJumbotrons.forEach((videoJumbotron) => {
                // if (mediaType == "image") this.getImagesAndMeasure(jumbotron);
                if (mediaType == "video") this.getVideosAndMeasure(videoJumbotron);
            });

        }
    }

    setBackgroundElements(backgroundElements) {
        this._backgroundElements = backgroundElements;
    }

    getBackgroundElements() {
        return this._backgroundElements;
    }

    measureImage(backgroundElement, element, colorizeElement = false) {
        let canvas = document.createElement("canvas");
        let context = canvas.getContext("2d");
    
        let image = new Image();
        image.src = backgroundElement.dataset.image;
    
        image.onload = () => {
            canvas.width = image.width;
            canvas.height = image.height;
    
            const { r, g, b } = calculateAverageRGB(canvas, context, image, image.width, image.height);
    
            // console.log(`Average color: rgb(${r}, ${g}, ${b})`);

            const jumbotronOrElement = element ? element : backgroundElement;
    
            // Call the onMeasured function with the average color
            this.onMeasured([r, g, b], colorizeElement, jumbotronOrElement);
        };
    }

    // find all videos in jumbotron, create canvas for each video and start the video measuring
    getVideosAndMeasure(element) {
        const timeUpdateHandler = (event) => {
            const video = event.target;
        
            if (Math.abs(video.currentTime - (video.previousTimerUpdate || 0)) > 1 || !video.previousTimerUpdate) {
                video.previousTimerUpdate = video.currentTime;
                this.measureVideo(video, element);
            }
        };
    
        let videoElements = element.querySelectorAll("video");
        const observerCallback = (entries, observer) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    // Measure video on the first frame. When color has been set once, play the video
                    // This way, the video only starts playing when the hero has been set
                    this.measureVideo(entry.target, element);

                    // Video is visible, play the video
                    entry.target.play();
        
                    // Ensure previousTimerUpdate is initialized for each video
                    if (!entry.target.previousTimerUpdate) {
                        entry.target.previousTimerUpdate = 0;
                    }
    
                    entry.target.addEventListener("timeupdate", timeUpdateHandler);
                } else {
                    entry.target.pause();
    
                    entry.target.removeEventListener("timeupdate", timeUpdateHandler);
                }
            });
        };
    
        const observer = new IntersectionObserver(observerCallback, {
            root: null,
            threshold: 0.1 // trigger if at least 10% of the video is visible
        });
    
        videoElements.forEach((video) => {
            let closestJumbotronVideo = video.closest(".jumbotron-video");
            if (closestJumbotronVideo) {    
                observer.observe(video);
            }
        });
    }

    measureVideo(video, element) {
        let canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        if (video && canvas && element) {
            // Create a canvas and draw the video frame on it, which is used to measure the color
            const context = canvas.getContext("2d");
            // const { r, g, b } = this.calculateAverageRGB(canvas, context, video, video.videoWidth, video.videoHeight);

            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            const backgroundColorApproximation = context.getImageData(
                0,
                0,
                canvas.width,
                canvas.height,
                { willReadFrequently: true }
            );

            const [r, g, b] = extractAvgColor(backgroundColorApproximation);
            const rgb = [r, g, b];

            this.onMeasured([r, g, b], false, element);
        }
    }

    
    onMeasured(rgb, colorizeElement, element) {
        const [r, g, b] = rgb;
        // console.log('onMeasured', r, g, b);
        // const { rgba, grayScale, grayScaleAsFactor, grayScaleInversion } = this.calculateLuminosity(r, g, b);

        const { whiteContrast, blackContrast } = calculateContrast(r, g, b)
        // console.log('whiteContrast', whiteContrast);
        // console.log('blackContrast', blackContrast);
        // console.log('_minimumContrastRatio', this._minimumContrastRatio);
        let luminosityString = whiteContrast > blackContrast ? 'white': 'black';
        let luminosityStringInversion = luminosityString === 'white' ? 'black' : 'white';
        let color = new Color(luminosityString);
        let colorInversion = new Color(luminosityStringInversion);
        // console.log('luminosityString would be ', luminosityString, 'for element', element);
        // console.log('luminosityStringInversion would be ', luminosityStringInversion, 'for element', element);
        // console.log('color would be', color.toString({ format: 'rgba' }));
        // console.log('colorInversion would be', colorInversion.toString({format: 'rgba'}));

        let rgba = color.toString({ format: 'rgba' });
        let grayScale = (color.lch.l / 100) * 255;
        // console.log('grayScale', grayScale);
        let grayScaleAsFactor = grayScale / 255;
        let grayScaleInversion = (1 - grayScaleAsFactor) * 255;
        this.setRGBAndGrayScale(rgba, element, grayScale, grayScaleInversion);

        const firstJumbotron = document.querySelector(".jumbotron");
        const parentJumbotron = element.closest('.jumbotron');

        const isJumboElement = element.classList.contains("jumbotron") || element.classList.contains("jumbotron-video");

        // If we are measuring in the jumobtron
        if ((firstJumbotron == parentJumbotron && isJumboElement) || element == firstJumbotron) {
            this.setHeader(firstJumbotron, luminosityString);
            this.setRGBAndGrayScale(rgba, this.header, grayScale, grayScaleInversion);
        } else {
            const themeToSet = luminosityString === 'white' ? 'dynamic-dark' : 'dynamic-light';
            this.setTheme(element, themeToSet);
        }

        if (colorizeElement) this.colorizeElement(element, grayScaleAsFactor);
        
    }

    setHeader(element, luminosityString) {
        const header = this.header;
        if (typeof this.configuration.mode !== 'undefined') {
            this.setTheme(element, this.configuration.mode);
            return;
        }

        // luminosityString: white || black
        // map lumonisityString to jumbotron color modes classes 'dynamic-light' || 'dynamic-dark'
        if (luminosityString === 'white') {
            this.setTheme(element, 'dynamic-dark');
            this.setTheme(header, 'dynamic-dark');
        }
        if (luminosityString === 'black') {
            this.setTheme(element, 'dynamic-light');
            this.setTheme(header, 'dynamic-light');
        }
    }

    getMinimumContrastRatio() {
        let body = document.documentElement.querySelector('body');
        if (body) {
            let contrastRatio = getComputedStyle(body).getPropertyValue('--nexus-minimum-contrast-ratio');
            if (contrastRatio) {
                return contrastRatio;
            }
        }
        return null;
    }

    calculateLuminosity(r, g, b) {
        const a = 1;
        let grayScale = 0.3 * r + 0.59 * g + 0.11 * b;
        let grayScaleAsFactor = grayScale / 255;
        let grayScaleInversion = (1 - grayScaleAsFactor) * 255;
        const rgba =
            "rgba(" +
            grayScaleInversion.toFixed(0) +
            "," +
            grayScaleInversion.toFixed(0) +
            "," +
            grayScaleInversion.toFixed(0) +
            "," +
            a +
            ")";
        return { rgba, grayScale, grayScaleAsFactor, grayScaleInversion };
    }

    setRGBAndGrayScale(rgba, element, grayScale, grayScaleInversion) {
        element.style.setProperty("--bg-background-rgb", rgba);
        element.style.setProperty(
            "--bg-background-grayscale",
            grayScale.toFixed(0)
        );
        element.style.setProperty(
            "--bg-background-grayscale-inversion",
            grayScaleInversion.toFixed(0)
        );
    }

    setTheme(element, modeToSet) {
        const classToSet =
            modeToSet === this.lightThemeClass ? this.lightThemeClass : this.darkThemeClass;
        const classToRemove =
            modeToSet === this.lightThemeClass ? this.darkThemeClass : this.lightThemeClass;

        element.classList.add(classToSet);
        element.classList.remove(classToRemove);
    }

    // setHeader(element, grayScaleAsFactor) {
    //     // select header for coloring based on FIRST jumbotron
    //     const header = this.header;

    //     // get first, for coloring header based on first jumbotron in content
    //     const modeToSet = typeof this.configuration.mode === 'undefined'
    //         ? (grayScaleAsFactor < 0.5 ? 'dark' : 'light')
    //         : this.configuration.mode.replace('dynamic-', '');

    //     this.setTheme(element, modeToSet);
    //     this.setTheme(header, modeToSet);
    // }

    colorizeElement(element, grayScaleAsFactor) {
        const mode =
            this.configuration.mode || (grayScaleAsFactor < 0.5 ? "dark" : "light");
        // this.setTheme(element, mode);
    }
}